import type { VehicleId } from '../../types/gcs/vehicle';
import type { GcsData } from '../../types/gcs/gcsData';
import { selectVehicleStatus } from './selectVehicleStatus';
import { VehicleStatusCategory, getVehicleStatusCategory } from '../../types/ui/vehicleStatusCategory';
import { VehicleStatus } from '../../types/ui/vehicleStatus';

/**
 * Selector to determine if a vehicle is under the current user's control
 * A vehicle is under user's control if:
 * - RPIC is in control for the vehicle's current mission (inflight nominal, inflight failure mode, waiting on dock, or ready for takeoff from dock), OR
 * - The vehicle status is READY_FOR_TAKEOFF (from launchpad)
 *
 * @param data - The GCS data map from the store
 * @param gcsId - The GCS ID
 * @param vehicleId - The vehicle ID
 * @returns true if the vehicle is under the user's control
 */
export function selectIsVehicleUnderMyControl(
  data: Map<string, GcsData>,
  gcsId: string,
  vehicleId: VehicleId
): boolean {
  const gcsData = data.get(gcsId);
  if (!gcsData) return false;

  // Get the current mission for this vehicle
  const mission = gcsData.liveMissionsMap.get(vehicleId);
  if (!mission) return false;

  const vehicleStatus = selectVehicleStatus(data, gcsId, vehicleId);
  const statusCategory = vehicleStatus ? getVehicleStatusCategory(vehicleStatus) : null;

  // Check if RPIC is in control for this mission
  const rpicInControl = gcsData.missionRpicInControlMap.get(mission.missionId);
  if (
    (statusCategory === VehicleStatusCategory.INFLIGHT_NOMINAL ||
      statusCategory === VehicleStatusCategory.INFLIGHT_FAILURE_MODE ||
      statusCategory === VehicleStatusCategory.WAITING_ON_DOCK ||
      vehicleStatus === VehicleStatus.READY_FOR_TAKEOFF_FROM_DOCK) &&
    rpicInControl?.isMissionRpic
  ) {
    return true;
  }

  return vehicleStatus === VehicleStatus.READY_FOR_TAKEOFF;
}
